import { Badge, Card, Group, Stack, Text, Title } from "@mantine/core";
import { memo, type FC } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type { Dataset, DatasetVersion } from "../types/dataset.js";
import type { AccountInfo } from "../../../../types/account";
import { useLocale } from "../../../../hooks/locale";
import { getLocaleString } from "@project/common";

interface DatasetItemProps {
  dataset: Dataset;
  version: DatasetVersion | null;
  account: AccountInfo;
}

export const DatasetItem: FC<DatasetItemProps> = memo(({ dataset, version }) => {
  const intl = useIntl();
  const locale = useLocale();
  const { scope, displayName, description, key } = dataset;

  const displayNameText = getLocaleString(locale, displayName);
  const descriptionText = getLocaleString(locale, description);

  return (
    <Card withBorder p="sm">
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={4} fw={600}>
            {displayNameText}
          </Title>
          <Badge color={scope === "private" ? "blue" : "green"} variant="outline" radius="xl">
            {scope === "private"
              ? <FormattedMessage defaultMessage="Private" id="label.private" />
              : <FormattedMessage defaultMessage="Free" id="label.free" />}
          </Badge>
        </Group>

        {descriptionText && (
          <Text size="sm" c="dimmed">
            {descriptionText}
          </Text>
        )}

        <Group gap="xs">
          <Text size="sm" c="dimmed">
            {key}
          </Text>
          {version?.createdAt && (
            <>
              <Text size="sm" c="dimmed">&middot;</Text>
              <Text size="sm" c="dimmed">
                {intl.formatDate(version.createdAt, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </Text>
            </>
          )}
        </Group>
      </Stack>
    </Card>
  );
});

DatasetItem.displayName = "DatasetItem";
